import { useCallback, useEffect, useRef, useState } from 'react';
import { ObsEncoderInfo, VideoCompositionInfo } from '../../types/obs';
import { promisefy } from '../../utils/promisefy';
import { ObsEncoderClassSelector } from './ObsEncoderClassSelector';
import { ObsPropertiesEditor } from './ObsPropertiesEditor';
import { getIsEncoderSettingsEnabled, getIsEncoderPropertiesQueryEnabled, getIsVideoEncoderScalingEnabled } from '../../constants/common';
import { getAvailableEncoders } from '../../constants/encoders';
import { CompositionsService } from '../../services/CompositionsService';
import { SelectSimple } from '../Select';

const encoderFieldSuffixMap = {
  bitrate: 'Kbps',
};

function VideoScaleEditor({ videoCompositionId, defaultValue, disabled, onChange }: {
  videoCompositionId: string,
  defaultValue: ObsEncoderInfo,
  disabled?: boolean,
  onChange: (newValue: ObsEncoderInfo) => void,
}) {
    const [isFeatureEnabled, setIsFeatureEnabled] = useState(false);
    const [videoComposition, setVideoComposition] = useState<VideoCompositionInfo | null>(null);
    const [options, setOptions] = useState<{ width?: number; height?: number; label: string }[]>([]);
    const [value, setValue] = useState<ObsEncoderInfo>(defaultValue);

    const handleChange = useCallback((newValue: string) => {
      console.log('newValue: ', newValue);

      const [ width, height ] = newValue.split('x').map((i) => {
        const num = Number.parseInt(i);

        if (isFinite(num)) return num;

        return 0;
      });

      const val = {
        ...value,
        width,
        height,
      };

      setValue(val);

      onChange?.(val);
    }, [value, setValue, onChange]);

    const key = useCallback((args: { width?: number; height?: number }) => {
      console.log('key: ', args);
      return `${args.width || 0}x${args.height || 0}`;
    }, []);

    useEffect(() => {
      const abortController = new AbortController();

      function fix(input: number): number {
        const num = Math.floor(input);

        return num - (num % 4);
      }

      const init = async () => {
        const isEnabled = await getIsVideoEncoderScalingEnabled();

        if (abortController.signal.aborted) return;

        if (!isEnabled) return;

        const composition = await CompositionsService.getCompositionFromMemory(videoCompositionId);

        if (abortController.signal.aborted) return;

        if (!composition) return;

        const isHorizontal = composition.videoFrame.width > composition.videoFrame.height;

        const allOptions: [ number, string ][] = 
          isHorizontal
          ? [
              [240, '240p'],
              [360, '360p'],
              [480, '480p (SD)'],
              [720, '720p (HD)'],
              [1080, '1080p (Full HD)'],
              [1440, '1440p (Quad HD)'],
              [1080, '1080p (2K)'],
              [2160, '2160p (4K, Ultra HD)'],
              [4320, '4320p (8K, Full Ultra HD)'],
          ] : [
              [240, '240p Vertical'],
              [360, '360p Vertical'],
              [480, '480p (SD) Vertical'],
              [720, '720p (HD) Vertical'],
              [1080, '1080p (Full HD) Vertical'],
              [1440, '1440p (Quad HD) Vertical'],
              [1080, '1080p (2K) Vertical'],
              [2160, '2160p (4K, Ultra HD) Vertical'],
              [4320, '4320p (8K, Full Ultra HD) Vertical'],
          ];

        const maxValue = Math.max(composition.videoFrame.width, composition.videoFrame.height);

        const newOptions = allOptions.filter((i) => {
          return i[0] <= maxValue;
        }).map((i) => {
          if (isHorizontal) {
            const aspect = composition.videoFrame.width / composition.videoFrame.height;

            const width = fix(i[0] * aspect);

            return {
              width,
              height: i[0],
              label: `${width}x${i[0]} ${i[1]}`,
            };
          } else {
            const aspect = composition.videoFrame.height / composition.videoFrame.width;

            const height = fix(i[0] * aspect);

            return {
              width: i[0],
              height,
              label: `${i[0]}x${height} ${i[1]}`,
            };
          }
        }).filter((i) => {
          return i.width !== composition.videoFrame.width || i.height !== composition.videoFrame.height;
        }) as { width?: number; height?: number; label: string }[];

        newOptions.unshift({
          width: undefined,
          height: undefined,
          label: `❎ Do Not Scale (${composition.videoFrame.width}x${composition.videoFrame.height})`,
        });

        setIsFeatureEnabled(isEnabled);
        setOptions(newOptions);
        setVideoComposition(composition);
      };

      init();

      return () => {
        abortController.abort();
      };
    }, [videoCompositionId, setVideoComposition, setIsFeatureEnabled]);

    if (!isFeatureEnabled) return null;

    if (!videoComposition) return null;

    return (<>
      <SelectSimple
        disabled={disabled}
        label={'Scale Video Frame'}
        options={new Map(options.map((item) => { return [key(item), item.label]; }))}
        //disabledOptions={new Set(availableEncoders.filter((item) => item.isDeprecated).map((item) => item.class))}
        //value={key(value.current)}
        defaultValue={key(value)}
        onChange={handleChange}
      />
    </>);
}

export function ObsEncoderEditor({type, defaultValue, videoCompositionId, disabled, showScaleVideoFrame, onChange} : {
    type: 'audio' | 'video',
    defaultValue: ObsEncoderInfo | undefined | null,
    videoCompositionId: string,
    disabled?: boolean,
    showScaleVideoFrame: boolean,
    onChange?: (value: ObsEncoderInfo) => void,
}) {
    const [hasAdvancedEncodingSettingsAccess, setHasAdvancedEncodingSettingsAccess] = useState(false);
    const [hasEncodingPropertiesQuery, setHasEncodingPropertiesQuery] = useState(false);
    const [availableEncoders, setAvailableEncoders] = useState<ObsEncoderInfo[]>([]);
    const value = useRef<ObsEncoderInfo | null | undefined>(defaultValue);

    useEffect(() => {
      if (availableEncoders.length) {
        return;
      }

      const abortController = new AbortController();

      getAvailableEncoders(type).then((encoders) => {
        if (abortController.signal.aborted) {
          return;
        }

        setAvailableEncoders(encoders.filter((item) => {
          return item.class === defaultValue?.class || !item.isDeprecated;
        }));

        if (!value.current) {
          if (defaultValue) {
            value.current = encoders.find((encoder) => encoder.class === defaultValue.class);

            if (value.current) {
              value.current.settings = defaultValue.settings;
              value.current.width = defaultValue.width;
              value.current.height = defaultValue.height;
            }
          } 
          
          if (!value.current) {
            value.current = encoders[0];

            onChange?.(value.current);
          }
        }
      });

      return () => {
        abortController.abort();
      };
    }, [defaultValue, availableEncoders, onChange, type]);

    useEffect(() => {
      if (hasAdvancedEncodingSettingsAccess) {
        return;
      }
  
      const abortController = new AbortController();
  
      Promise.all([
        getIsEncoderSettingsEnabled(),
        getIsEncoderPropertiesQueryEnabled(),
      ]).then(([ encoderSettingsEnabled, encoderPropsQueryEnabled ]) => {
        if (abortController.signal.aborted) {
          return;
        }
  
        if (encoderSettingsEnabled !== hasAdvancedEncodingSettingsAccess) {
          setHasAdvancedEncodingSettingsAccess(encoderSettingsEnabled);
        }

        if (encoderPropsQueryEnabled !== hasEncodingPropertiesQuery) {
          setHasEncodingPropertiesQuery(encoderPropsQueryEnabled);
        }
      });
  
      return () => {
        abortController.abort();
      };
    }, [hasAdvancedEncodingSettingsAccess, hasEncodingPropertiesQuery]);

    return (<>
      <ObsEncoderClassSelector
        disabled={disabled}
        type={type}
        label={type === 'video' ? 'Video Encoding' : 'Audio Encoding'}
        value={value.current?.class}
        onChange={(encoderClass) => {
          const curr = availableEncoders.find((item) => item.class === encoderClass);

          if (curr) {
            value.current = curr;

            onChange?.(value.current);
          }
        }}
      />

      
      { value.current && showScaleVideoFrame && <>
        <VideoScaleEditor videoCompositionId={videoCompositionId} defaultValue={value.current} disabled={disabled} onChange={( newValue: ObsEncoderInfo ) => {
          if (!value.current) return;

          value.current.width = newValue.width;
          value.current.height = newValue.height;

          onChange?.(value.current);
        }} />
      </> }

      { hasAdvancedEncodingSettingsAccess && <>
        <ObsPropertiesEditor
          disabled={disabled}
          properties={value.current?.properties ?? []}
          settings={value.current?.settings ?? {}}
          defaultSettings={value.current?.defaultSettings ?? {}}
          suffixes={encoderFieldSuffixMap ?? {}}
          onChange={async (data) => {
            let curr: ObsEncoderInfo | null | undefined = value.current;

            if (!curr) {
              return;
            }

            curr.settings = data;

            if (hasEncodingPropertiesQuery) {
              const promise = type === 'video' ? promisefy(window.host.getAvailableVideoEncoderClassProperties) : promisefy(window.host.getAvailableAudioEncoderClassProperties);

              curr = await promise(curr);
            }

            if (!curr) {
              return;
            }

            value.current = curr;

            onChange?.(value.current);
          }}
        />
      </> }
    </>);
}
